import { Col, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import InputDecimal from "../../../components/Forms/Element/InputDecimal";
import InputDate from "../../../components/Forms/Element/InputDate";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import SegmentedButtons from "../../../components/Forms/Element/SegmentedButtons";
import ProductSelect from "../../../components/Custom/Main/Product/ProductSelect";
import CategorySelect from "../../../components/Custom/Main/Category/CategorySelect";
import EmployeeSelect from "../../../components/Custom/Main/Employee/EmployeeSelect";
import BranchSelect from "../../../components/Custom/Main/Branch/BranchSelect";
import StatusCheckBox from "../../../components/Common/StatusCheckbox";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import FormSection from "../../../components/Common/Form/FormSection";
import HintTooltip from "../../../components/Common/HintTooltip";
import { convertStatusToBoolean } from "../../../../utils/ConverterUtils";
import { parseYYYMMDDToDate } from "../../../../utils/DateUtils";
import { useBranch } from "../../../../context/BranchContext";
import { getScopeOptions, toFormScope, SCOPE_VALUE } from "./ScopeOptions";
import TierEditor from "./TierEditor";
import CommissionRuleSummary from "./CommissionRuleSummary";

const Form = (props) => {
    const { t } = useTranslation();
    const { status, setStatus, formik, entityData } = props;
    const { selectedBranch } = useBranch();

    const scopeTypeOptions = getScopeOptions(t);

    const scopeType = formik.values.scopeType?.value;

    // A comissão por faixa só existe na abrangência "somente produtos", e o backend recusa as
    // demais.
    const allowsTiered = scopeType === SCOPE_VALUE.ONLY_PRODUCTS;

    // A opção indisponível continua na tela, desabilitada, em vez de ser removida. Sozinho, o
    // botão restante lê como ação a executar e não como opção escolhida; é o contraste com a
    // outra que faz o conjunto parecer um seletor. Quem explica o motivo é a legenda abaixo,
    // que aparece junto do estado desabilitado.
    const paymentTypeOptions = [
        { value: 'FIXED', label: t('commission_payment_fixed'), icon: 'fa-solid fa-percent' },
        {
            value: 'TIERED',
            label: t('commission_payment_tiered'),
            icon: 'fa-solid fa-stairs',
            disabled: !allowsTiered
        }
    ];

    // Abrangência que não aceita faixa força o percentual fixo, que é a única forma de pagar
    // que sobra. Fica num efeito, e não dentro do handler do select, porque a abrangência
    // também muda ao carregar uma regra para edição e ao resetar o formulário: preso ao
    // handler, esses caminhos deixariam "por faixa" selecionado com o botão invisível, e o
    // operador só descobriria ao salvar.
    useEffect(() => {
        if (!allowsTiered && formik.values.paymentType !== 'FIXED') {
            formik.setFieldValue('paymentType', 'FIXED');
        }

        // eslint-disable-next-line
    }, [allowsTiered]);

    useEffect(() => {
        if (entityData != null) {
            // "Somente produtos" e "somente serviços" chegam como um escopo só do backend,
            // então a opção da tela precisa considerar o tipo junto para reabrir a certa.
            const selectedScopeValue = toFormScope(entityData.scopeType, entityData.scopeProductType);
            const selectedScope = scopeTypeOptions.find(option => option.value === selectedScopeValue);
            formik.setFieldValue('scopeType', selectedScope || null);
            formik.setFieldValue('paymentType', entityData.paymentType || 'FIXED');
            formik.setFieldValue('percent', entityData.percent);
            // Tabela vazia ainda precisa da primeira linha travada em zero, senão a tela abre
            // sem lugar nenhum para digitar.
            formik.setFieldValue('tiers', entityData.tiers?.length
                ? entityData.tiers.map(tier => ({ minRevenue: tier.minRevenue, percent: tier.percent }))
                : [{ minRevenue: 0, percent: '' }]);
            formik.setFieldValue('validFrom', entityData.validFrom ? parseYYYMMDDToDate(entityData.validFrom) : null);
            formik.setFieldValue('validTo', entityData.validTo ? parseYYYMMDDToDate(entityData.validTo) : null);

            // O alvo volta como opção do select usando o nome já resolvido pelo backend, para
            // a edição não abrir com o campo vazio e o operador achar que precisa reescolher.
            if (entityData.scopeId) {
                formik.setFieldValue('scope', {
                    value: entityData.scopeId,
                    label: entityData.scopeName || `#${entityData.scopeId}`
                });
            }
            if (entityData.employee) {
                formik.setFieldValue('employee', {
                    value: entityData.employee.id,
                    label: entityData.employee.fullName
                });
            }
            if (entityData.branch) {
                formik.setFieldValue('branch', {
                    value: entityData.branch.id,
                    label: entityData.branch.name
                });
            }

            setStatus(convertStatusToBoolean(entityData.status));
        }

        // eslint-disable-next-line
    }, [entityData]);

    // Com filial selecionada no cabeçalho, ela é aplicada e travada no formulário, mesmo
    // padrão do restante dos cadastros: o operador está trabalhando dentro daquela filial e
    // não deveria criar regra que afeta outra sem perceber. Sem filial selecionada (visão
    // geral), o campo fica livre e em branco significa "vale para todas".
    useEffect(() => {
        if (selectedBranch) {
            formik.setFieldValue('branch', {
                value: selectedBranch.id,
                label: selectedBranch.name
            });
        }

        // eslint-disable-next-line
    }, [selectedBranch]);

    // Trocar o tipo de alvo invalida o alvo escolhido: um id de produto não faz sentido
    // quando o escopo vira categoria. A forma de pagar é ajustada pelo efeito acima, que
    // cobre também a edição e o reset.
    const handleScopeTypeChange = (option) => {
        formik.setFieldValue('scopeType', option);
        formik.setFieldValue('scope', null);
    };

    const isEditing = entityData != null;

    return (
        <>
            {/* O formulário segue as perguntas que o dono faz ao combinar uma comissão: para quem,
                em quê, quanto e desde quando. Antes ele abria pela "abrangência" e deixava o
                colaborador como campo opcional no meio do percentual, na ordem do modelo de
                dados e não na do raciocínio de quem cadastra. */}
            <div className="form-layout form-layout--spaced">
                <FormSection
                    title={t('commission_rule_who_title')}
                    description={t('commission_rule_who_hint')}
                    icon="fa-solid fa-user-group"
                >
                    {/* A filial vem primeiro porque ela filtra o colaborador: escolher a pessoa
                        antes da unidade listaria quem não trabalha nela. Mesma ordem do cadastro
                        de vales. */}
                    <Row className="g-3">
                        <Col md={6}>
                            <FormGroup>
                                <FormLabel>{t('branch')}</FormLabel>
                                <BranchSelect
                                    value={formik.values.branch}
                                    onChange={(option) => {
                                        formik.setFieldValue('branch', option);
                                        // O colaborador escolhido pode não trabalhar na filial nova.
                                        formik.setFieldValue('employee', null);
                                    }}
                                    isDisabled={!!selectedBranch}
                                    placeholder={t('commission_rule_all_branches')}
                                />
                                {/* Com a filial travada pelo cabeçalho, explica por que o campo
                                    não abre, em vez de o usuário achar que é defeito. */}
                                {selectedBranch && (
                                    <small className="text-muted d-block mt-1">
                                        {t('commission_rule_branch_locked', { branch: selectedBranch.name })}
                                    </small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <FormLabel>{t('employee')}</FormLabel>
                                {/* Vazio vale para todos, e o próprio campo diz isso. A lista segue a
                                    filial escolhida ao lado; a key recarrega o select quando ela muda,
                                    senão ele mantinha em cache os colaboradores da filial anterior. */}
                                <EmployeeSelect
                                    key={formik.values.branch?.value ?? 'all'}
                                    branch={formik.values.branch}
                                    value={formik.values.employee}
                                    onChange={(option) => formik.setFieldValue('employee', option)}
                                    placeholder={t('commission_rule_all_employees')}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>

                <FormSection
                    title={t('commission_rule_what_title')}
                    description={t('commission_rule_what_hint')}
                    icon="fa-solid fa-scissors"
                >
                    <Row className="g-3">
                        <Col md={6}>
                            <FormGroup>
                                <FormLabel>{t('commission_rule_applies_to')} <AsteriskRequired /></FormLabel>
                                <CustomSelect
                                    options={scopeTypeOptions}
                                    value={formik.values.scopeType}
                                    onChange={handleScopeTypeChange}
                                    placeholder={t('select')}
                                    error={formik.errors.scopeType}
                                />
                            </FormGroup>
                        </Col>

                        {/* O campo do item só aparece quando há o que escolher. Serviço também é
                            Product no modelo, e o select já lista os dois; o rótulo diz isso para
                            ninguém procurar o corte de cabelo em outro lugar. */}
                        {scopeType === SCOPE_VALUE.PRODUCT && (
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>{t('product_or_service')} <AsteriskRequired /></FormLabel>
                                    <ProductSelect
                                        value={formik.values.scope}
                                        onChange={(option) => formik.setFieldValue('scope', option)}
                                    />
                                    {formik.errors.scope && (
                                        <div className="text-danger fs-12 mt-1">{formik.errors.scope}</div>
                                    )}
                                </FormGroup>
                            </Col>
                        )}

                        {scopeType === SCOPE_VALUE.CATEGORY && (
                            <Col md={6}>
                                <FormGroup>
                                    {/* A dica fica só aqui: categoria marcada para não gerar comissão
                                        anula a regra, e isso não dá para descobrir pela tela. */}
                                    <FormLabel>{t('category')} <AsteriskRequired /> <HintTooltip text={t('commission_rule_field_category_hint')} /></FormLabel>
                                    <CategorySelect
                                        value={formik.values.scope}
                                        onChange={(option) => formik.setFieldValue('scope', option)}
                                    />
                                    {formik.errors.scope && (
                                        <div className="text-danger fs-12 mt-1">{formik.errors.scope}</div>
                                    )}
                                </FormGroup>
                            </Col>
                        )}
                    </Row>
                </FormSection>

                <FormSection
                    title={t('commission_rule_how_much_title')}
                    description={t('commission_rule_how_much_hint')}
                    icon="fa-solid fa-percent"
                >
                    {/* A forma de pagar vem antes do valor porque decide qual campo aparece embaixo. */}
                    <Row className="g-3 mb-3">
                        <Col md={12}>
                            <FormLabel>{t('commission_payment_type')}</FormLabel>
                            <SegmentedButtons
                                ariaLabel={t('commission_payment_type')}
                                value={formik.values.paymentType}
                                onChange={(value) => formik.setFieldValue('paymentType', value)}
                                options={paymentTypeOptions.map(option => ({
                                    ...option,
                                    title: option.disabled ? t('commission_payment_tiered_only_products') : undefined
                                }))}
                            />
                            {/* Opção desabilitada sem motivo parece defeito. */}
                            {!allowsTiered && (
                                <div className="fs-13 text-muted mt-2">
                                    <i className="fa-solid fa-circle-info me-2" aria-hidden="true"></i>
                                    {t('commission_payment_tiered_only_products')}
                                </div>
                            )}
                        </Col>
                    </Row>

                    {formik.values.paymentType === 'TIERED' ? (
                        <TierEditor
                            tiers={formik.values.tiers}
                            onChange={(tiers) => formik.setFieldValue('tiers', tiers)}
                            error={formik.errors.tiers}
                        />
                    ) : (
                        <Row className="g-3">
                            <Col md={4}>
                                <FormGroup>
                                    <FormLabel>{t('commission')} (%) <AsteriskRequired /> <HintTooltip text={t('commission_rule_field_percent_hint')} /></FormLabel>
                                    <InputDecimal
                                        name="percent"
                                        onValueChange={(values) => formik.setFieldValue('percent', values.floatValue ?? '')}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.percent}
                                        error={formik.errors.percent}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                </FormSection>

                <FormSection
                    title={t('commission_rule_when_title')}
                    description={t('commission_rule_when_hint')}
                    icon="fa-solid fa-calendar-days"
                >
                    <Row className="g-3">
                        <Col md={4}>
                            <FormGroup>
                                <FormLabel>{t('commission_rule_starts_on')} <AsteriskRequired /></FormLabel>
                                <InputDate
                                    selected={formik.values.validFrom}
                                    onChange={(date) => formik.setFieldValue('validFrom', date)}
                                />
                                {/* O aviso fica colado no campo porque é aqui que o erro acontece:
                                    começar hoje deixa sem comissão o que já foi vendido no mês. */}
                                <small className="text-muted d-block mt-1">{t('commission_rule_starts_on_help')}</small>
                                {formik.errors.validFrom && (
                                    <div className="text-danger fs-12 mt-1">{formik.errors.validFrom}</div>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <FormLabel>{t('commission_rule_ends_on')}</FormLabel>
                                <InputDate
                                    selected={formik.values.validTo}
                                    onChange={(date) => formik.setFieldValue('validTo', date)}
                                />
                                <small className="text-muted d-block mt-1">{t('commission_rule_ends_on_help')}</small>
                                {formik.errors.validTo && (
                                    <div className="text-danger fs-12 mt-1">{formik.errors.validTo}</div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>

                {/* Regra nova nasce ativa: o liga/desliga só aparece na edição, que é quando
                    alguém quer pausar uma regra sem apagá-la. */}
                {isEditing && (
                    <FormSection
                        title={t('status')}
                        description={t('commission_rule_status_hint')}
                        icon="fa-solid fa-toggle-on"
                        compact
                    >
                        <FormGroup className="mb-0">
                            <StatusCheckBox
                                name="status"
                                defaultChecked={status}
                                checked={status}
                                onClick={() => setStatus(!status)} />
                        </FormGroup>
                    </FormSection>
                )}

                <CommissionRuleSummary values={formik.values} />
            </div>
        </>
    );
}

export default Form;
